import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { MA_CATS, type CategoryId } from "@myams/shared";
import { MaIcon, type IconName } from "../components/ui";

const CAT_ORDER: { id: CategoryId; label: string }[] = [
  { id: "music",   label: "Music" },
  { id: "food",    label: "Food & Drink" },
  { id: "art",     label: "Art" },
  { id: "market",  label: "Markets" },
  { id: "cinema",  label: "Cinema" },
  { id: "outdoor", label: "Outdoors" },
  { id: "cafe",    label: "Cafés" },
  { id: "queer",   label: "Queer" },
  { id: "sports",  label: "Sports" },
  { id: "hidden",  label: "Hidden" },
];

const ICON_BY_CAT: Record<CategoryId, IconName> = {
  music: "music", food: "food", art: "art", market: "market",
  cinema: "cinema", outdoor: "outdoor", cafe: "cafe", queer: "queer",
  sports: "sports", hidden: "hidden",
};

interface Props {
  initial?: CategoryId[];
  onContinue?: (selected: CategoryId[]) => void;
}

export function Onboarding2({ initial = ["music", "food", "market", "outdoor", "cafe", "hidden"], onContinue }: Props) {
  const navigate = useNavigate();
  const [selected, setSelected] = useState<Set<CategoryId>>(new Set(initial));

  const toggle = (id: CategoryId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  return (
    <div className="ma-screen">
      <div style={{
        padding: "calc(env(safe-area-inset-top, 20px) + 44px) 24px 0",
        display: "flex", justifyContent: "space-between", alignItems: "center",
      }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            background: "transparent", border: "none", color: "var(--ma-ink)",
            fontFamily: "var(--ma-mono)", fontSize: 11, letterSpacing: "0.14em",
            textTransform: "uppercase", cursor: "pointer", padding: 0,
          }}
        >
          ← Back
        </button>
        <div className="ma-mono" style={{ fontSize: 10, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--ma-ink-soft)" }}>
          step 02 / 03
        </div>
      </div>

      <div style={{ padding: "24px 24px 16px" }}>
        <div className="ma-mono" style={{ fontSize: 10, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--ma-brick)", marginBottom: 8 }}>
          tune your radar
        </div>
        <div className="ma-display" style={{ fontSize: 40, lineHeight: 0.95, color: "var(--ma-ink)", marginBottom: 12 }}>
          What gets you<br />out the door?
        </div>
        <div style={{ fontFamily: "var(--ma-body)", fontSize: 14, color: "var(--ma-ink-soft)" }}>
          Pick at least three. You can swap them later.
        </div>
      </div>

      <div style={{ padding: "12px 16px 110px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        {CAT_ORDER.map((c, i) => {
          const cat = MA_CATS[c.id];
          const sel = selected.has(c.id);
          return (
            <button key={c.id} onClick={() => toggle(c.id)} style={{
              position: "relative", padding: "16px 14px 14px",
              background: sel ? cat.color : "var(--ma-cream)",
              color: sel ? "var(--ma-paper)" : "var(--ma-ink)",
              border: "var(--ma-border) solid var(--ma-ink)",
              borderRadius: "var(--ma-card-radius)",
              cursor: "pointer", minHeight: 92, textAlign: "left",
              display: "flex", flexDirection: "column", justifyContent: "space-between",
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <MaIcon name={ICON_BY_CAT[c.id]} size={22} stroke={1.6} />
                {sel && (
                  <div style={{
                    width: 20, height: 20, borderRadius: "50%",
                    background: "var(--ma-paper)", color: "var(--ma-ink)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <MaIcon name="check" size={14} stroke={2.4} />
                  </div>
                )}
              </div>
              <div>
                <div className="ma-display" style={{ fontSize: 18, color: "inherit" }}>{c.label}</div>
                <div className="ma-mono" style={{ fontSize: 9, letterSpacing: "0.1em", textTransform: "uppercase", opacity: 0.7, marginTop: 2 }}>
                  {String(i + 1).padStart(2, "0")} / 10
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <div style={{
        position: "absolute", bottom: 0, left: 0, right: 0,
        padding: "16px 24px calc(env(safe-area-inset-bottom, 0px) + 24px)",
        background: "linear-gradient(to top, var(--ma-paper) 60%, transparent)",
        display: "flex", gap: 10, alignItems: "center",
      }}>
        <div style={{ flex: 1 }}>
          <div className="ma-mono" style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--ma-ink-soft)" }}>
            {selected.size} of 10 selected
          </div>
        </div>
        <button
          disabled={selected.size < 3}
          onClick={() => {
            const list = Array.from(selected);
            if (onContinue) onContinue(list);
            navigate("/signup", { state: { interests: list } });
          }}
          style={{
            padding: "16px 32px",
            background: selected.size < 3 ? "var(--ma-ink-soft)" : "var(--ma-brick)",
            color: "var(--ma-paper)",
            border: "var(--ma-border) solid var(--ma-ink)",
            borderRadius: "var(--ma-card-radius)",
            fontFamily: "var(--ma-display)", fontWeight: 700, fontSize: 15,
            letterSpacing: "0.02em",
            cursor: selected.size < 3 ? "not-allowed" : "pointer",
            display: "flex", alignItems: "center", gap: 8,
            opacity: selected.size < 3 ? 0.7 : 1,
          }}
        >
          Continue
          <MaIcon name="arrow" size={16} stroke={2.2} color="var(--ma-paper)" />
        </button>
      </div>
    </div>
  );
}
